/**
 * Utilities cho Activation Code management
 */

// Generate mã kích hoạt unique
export const generateActivationCode = (options = {}) => {
  const {
    prefix = 'BOOK',
    year = new Date().getFullYear(),
    length = 6,
    includeNumbers = true,
    includeLetters = true
  } = options;

  let chars = '';
  if (includeNumbers) chars += '0123456789';
  if (includeLetters) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  let randomString = '';
  for (let i = 0; i < length; i++) {
    randomString += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return `${prefix}-${year}-${randomString}`;
};

// Generate batch codes với số lượng nhất định
export const generateBatchCodes = (count, options = {}) => {
  const codes = [];
  const generated = new Set(); // Đảm bảo unique

  while (codes.length < count) {
    const code = generateActivationCode(options);
    if (!generated.has(code)) {
      generated.add(code);
      codes.push(code);
    }
  }

  return codes;
};

// Tạo QR Code (sử dụng external library hoặc service)
export const generateQRCode = async (code, options = {}) => {
  const {
    size = 200,
    format = 'png',
    errorCorrectionLevel = 'M'
  } = options;

  // Trong thực tế, bạn có thể sử dụng:
  // - qrcode library: https://www.npmjs.com/package/qrcode
  // - QR service API
  // - Canvas để generate

  // Mock implementation
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Draw simple placeholder
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(code, size / 2, size / 2);

  return canvas.toDataURL(`image/${format}`);
};

// Validate mã kích hoạt format
export const validateCodeFormat = (code) => {
  // Format: PREFIX-YEAR-RANDOM (e.g., BOOK-2024-ABC123)
  const regex = /^[A-Z]+-\d{4}-[A-Z0-9]{6}$/;
  return regex.test(code);
};

// Check expiry status
export const getCodeStatus = (code) => {
  const now = new Date();
  const expiryDate = new Date(code.expires_at);

  if (code.status === 'revoked') return 'revoked';
  if (code.used_at) return 'used';
  if (expiryDate < now) return 'expired';
  return 'unused';
};

// Format status display
export const getStatusDisplay = (status) => {
  const statusMap = {
    unused: { label: 'Chưa sử dụng', icon: '🔵', badgeClass: 'bg-blue-100 text-blue-800' },
    used: { label: 'Đã sử dụng', icon: '✅', badgeClass: 'bg-green-100 text-green-800' },
    expired: { label: 'Hết hạn', icon: '⏰', badgeClass: 'bg-red-100 text-red-800' },
    revoked: { label: 'Đã thu hồi', icon: '🚫', badgeClass: 'bg-gray-100 text-gray-800' }
  };

  return statusMap[status] || { label: 'Không xác định', icon: '❓', badgeClass: 'bg-gray-100 text-gray-800' };
};

// Lô/mã theo bộ sách có book_title = null, tên nằm ở collection_title
export const getTargetDisplay = (item) => {
  if (item?.collection_title) {
    return { isCollection: true, title: item.collection_title };
  }
  return { isCollection: false, title: item?.book_title || '' };
};

// Export codes to CSV
export const exportToCSV = (codes, filename = 'activation-codes.csv') => {
  const headers = [
    'Mã kích hoạt',
    'SKU',
    'Sách',
    'Trạng thái', 
    'Ngày tạo',
    'Ngày hết hạn',
    'Ngày sử dụng',
    'Người sử dụng'
  ];

  const csvContent = [
    headers.join(','),
    ...codes.map(code => [
      code.code,
      code.sku,
      `"${getTargetDisplay(code).title}"`,
      getStatusDisplay(code.status).label,
      formatDate(code.created_at),
      formatDate(code.expires_at),
      code.used_at ? formatDate(code.used_at) : '',
      code.used_by || ''
    ].join(','))
  ].join('\n');

  downloadFile(csvContent, filename, 'text/csv');
};

// Export codes to JSON
export const exportToJSON = (codes, filename = 'activation-codes.json') => {
  const jsonContent = JSON.stringify(codes, null, 2);
  downloadFile(jsonContent, filename, 'application/json');
};

// Helper function để download file
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('vi-VN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Calculate statistics
export const calculateStatistics = (codes) => {
  const total = codes.length;
  const unused = codes.filter(c => getCodeStatus(c) === 'unused').length;
  const used = codes.filter(c => getCodeStatus(c) === 'used').length;
  const expired = codes.filter(c => getCodeStatus(c) === 'expired').length;
  const revoked = codes.filter(c => getCodeStatus(c) === 'revoked').length;

  return {
    total,
    unused,
    used,
    expired,
    revoked,
    usage_rate: total > 0 ? ((used / total) * 100).toFixed(1) : 0,
    expiry_rate: total > 0 ? ((expired / total) * 100).toFixed(1) : 0
  };
};

// Group codes by batch/SKU
export const groupCodesBySKU = (codes) => {
  return codes.reduce((groups, code) => {
    const key = code.sku;
    if (!groups[key]) {
      groups[key] = {
        sku: key,
        book_title: code.book_title,
        codes: []
      };
    }
    groups[key].codes.push(code);
    return groups;
  }, {});
};

// Search/filter codes
export const filterCodes = (codes, filters) => {
  return codes.filter(code => {
    const matchesSearch = !filters.q || 
      code.code.toLowerCase().includes(filters.q.toLowerCase()) ||
      code.book_title.toLowerCase().includes(filters.q.toLowerCase());
    
    const matchesStatus = !filters.status || getCodeStatus(code) === filters.status;
    const matchesSKU = !filters.sku || code.sku === filters.sku;
    const matchesBook = !filters.book_id || code.book_id === filters.book_id;

    return matchesSearch && matchesStatus && matchesSKU && matchesBook;
  });
};
